import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { UnpaidPaymentsAdapterService, UnifiedProductOption, UnifiedUnpaidPayment } from '../../unpaid-payments-adapter.service';
import { ProductConfigService } from '../../../../../core/services/product-config.service';
import { UnpaidPartnerTileSummaryService } from '../../unpaid-partner-tile-summary.service';
import { PaginatedTableComponent, TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { UnpaidPaymentsCalculationService } from '../../unpaid-payments-calculation.service';
import { UnpaidPaymentsModalService } from '../../unpaid-payments-modal.service';
import {
  LendeeListRouteDefaults,
  LendeeListRouteState,
  buildLendeeListQueryParams,
  getDefaultIsDummy,
  parseLendeeListRouteState,
} from '../../unpaid-payments-route-query.util';
import { UNPAID_PAYMENTS_BULK_SELECTION_STYLES } from '../../unpaid-payments-bulk-selection.styles';
import { UnpaidPaymentsIsDummyService } from '../../unpaid-payments-is-dummy.service';
import { UnpaidPaymentsAccessService } from '../../unpaid-payments-access.service';
import { UnpaidPaymentsHubOverviewService } from '../../unpaid-payments-hub-overview.service';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { SelectionComponent } from '../../../../../shared/components/ui/selection/selection.component';
import { UnpaidPartnerOverviewCardsComponent } from '../../components/unpaid-partner-overview-cards.component';
import { UnpaidPaymentsIsDummyToggleComponent } from '../../components/unpaid-payments-is-dummy-toggle/unpaid-payments-is-dummy-toggle.component';
@Component({
  selector: 'app-unpaid-payments-lendee',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponentV2,
    PaginatedTableComponent,
    // SelectionComponent,
    UnpaidPartnerOverviewCardsComponent,
    UnpaidPaymentsIsDummyToggleComponent,
  ],
  templateUrl: './unpaid-payments-lendee.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [UNPAID_PAYMENTS_BULK_SELECTION_STYLES],
})
export class UnpaidPaymentsLendeeComponent implements OnInit {
  private readonly calculationService = inject(UnpaidPaymentsCalculationService);

  readonly productKey = 'lendee';
  readonly defaultStartDate = '2025-01-01';
  readonly defaultEndDate = this.calculationService.unpaidPaymentsTodayYmd();

  readonly loading = signal(false);
  readonly paying = signal(false);
  readonly tableBusy = computed(() => this.loading() || this.paying());
  readonly tableBusyMessage = computed(() =>
    this.paying() ? 'Processing payments...' : 'Loading unpaid records...'
  );
  readonly payAllButtonLabel = 'Pay All to Investors';
  readonly payments = signal<UnifiedUnpaidPayment[]>([]);
  readonly search = signal('');
  readonly startDate = signal(this.defaultStartDate);
  readonly endDate = signal(this.defaultEndDate);
  readonly endDateMax = computed(() => this.calculationService.unpaidPaymentsTodayYmd());
  readonly startDateMax = computed(() =>
    this.calculationService.resolveListStartDateMax(this.endDate(), this.endDateMax())
  );
  readonly subStatus = signal(-1);
  readonly selectedIds = signal<number[]>([]);
  readonly page = signal(1);
  readonly pageSize = 10;
  readonly totalCount = signal(0);
  readonly totalPages = signal(0);
  /** Matches legacy single-pay balance guard (`pay_to_user`). */
  readonly availableBalance = signal(0);
  readonly platformBalance = signal(0);
  /** Released payment ids from the full filtered listing — not the current table page. */
  readonly payableUspaIds = signal<number[]>([]);
  /** All uspa_ids from the API response (regardless of status). */
  readonly allUspaIds = signal<number[]>([]);
  readonly releasedPayableCount = signal(0);
  /** Sum of `pay_to_user` for released rows — not `platform_balance` (supporter total). */
  readonly releasedPayableAmount = signal(0);

  readonly offset = computed(() => (this.page() - 1) * this.pageSize);
  readonly canGoPrev = computed(() => this.page() > 1);
  readonly canGoNext = computed(() => this.page() < this.totalPages());
  readonly overview = computed(() => this.partnerTileSummary.overviewFor(this.productKey));
  readonly showUrgentOverviewAction = false;

  readonly selectedPayablePayments = computed(() =>
    this.payments().filter((p) => {
      if (!this.canPayRow(p)) return false;
      const id = this.rowPayId(p);
      return id !== null && this.selectedIds().includes(id);
    })
  );

  readonly totalSelectedAmount = computed(() =>
    this.selectedPayablePayments().reduce((sum, p) => sum + this.getAmount(p), 0)
  );

  readonly selectedPayableCount = computed(() => this.selectedPayablePayments().length);

  readonly selectedBusinessesCount = computed(() => {
    const ids = new Set<string>();
    for (const row of this.selectedPayablePayments()) {
      const bizId = row?.['investor_id'] ?? row?.['contract_id'];
      if (bizId !== undefined && bizId !== null && bizId !== '') {
        ids.add(String(bizId));
      }
    }
    return ids.size;
  });

  readonly selectedRows = computed(() =>
    this.payments().filter((p) => {
      const id = this.rowPayId(p);
      return id !== null && this.selectedIds().includes(id);
    })
  );

  readonly canSelectRowBind = (row: UnifiedUnpaidPayment) => this.canSelectRow(row);

  readonly tableHeaders: TableHeader[] = [
    { key: 'serialNo', label: 'S No.', sortable: false, colClass: 'whitespace-nowrap w-16' },
    { key: 'microloanId', label: 'Micro loan Id', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'contractId', label: 'Contract ID', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'platform', label: 'Platform', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'status', label: 'Status', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'supporter', label: 'Supporter Info', sortable: false, colClass: 'min-w-[220px]' },
    { key: 'paymentType', label: 'Payment Type', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'payableAmount', label: 'Payable Amount', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'action', label: 'Action', sortable: false, align: 'text-left', colClass: 'whitespace-nowrap' },
  ];

  private readonly paymentConfirmTitle =
    'Are you sure you want to pay the investor for this unpaid payment ?';

  private readonly destroyRef = inject(DestroyRef);
  private readonly isDummyService = inject(UnpaidPaymentsIsDummyService);
  private readonly accessService = inject(UnpaidPaymentsAccessService);
  private readonly hubOverviewService = inject(UnpaidPaymentsHubOverviewService);
  private lastRouteSignature = '';

  constructor(
    private readonly adapterService: UnpaidPaymentsAdapterService,
    private readonly productConfigService: ProductConfigService,
    private readonly partnerTileSummary: UnpaidPartnerTileSummaryService,
    private readonly unpaidPaymentsModal: UnpaidPaymentsModalService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nextState = parseLendeeListRouteState(params, this.lendeeRouteDefaults);
      const signature = JSON.stringify(nextState);
      if (signature === this.lastRouteSignature) {
        return;
      }
      this.lastRouteSignature = signature;
      this.applyRouteState(nextState);
      this.fetchPayments();
    });
  }

  private get lendeeRouteDefaults(): LendeeListRouteDefaults {
    return {
      startDate: this.defaultStartDate,
      endDate: this.defaultEndDate,
      subStatus: -1,
      isDummy: getDefaultIsDummy(),
    };
  }

  onIsDummyToggle(isDummy: boolean): void {
    this.navigateWithFilters({ isDummy });
  }

  fetchPayments(): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) {
      this.payments.set([]);
      this.totalCount.set(0);
      this.totalPages.set(0);
      this.payableUspaIds.set([]);
      this.allUspaIds.set([]);
      this.releasedPayableCount.set(0);
      this.releasedPayableAmount.set(0);
      return;
    }
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) {
      this.payments.set([]);
      this.totalCount.set(0);
      this.totalPages.set(0);
      this.payableUspaIds.set([]);
      this.allUspaIds.set([]);
      this.releasedPayableCount.set(0);
      this.releasedPayableAmount.set(0);
      return;
    }

    this.loading.set(true);
    this.adapterService
      .listPayments(this.productKey, config.productServerName, {
        search_string: this.search(),
        limit: this.pageSize,
        offset: this.offset(),
        sub_status: this.subStatus(),
        start_date: this.startDate(),
        end_date: this.endDate(),
      })
      .subscribe({
        next: (res: any) => {
          this.loading.set(false);
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          this.payments.set(Array.isArray(res?.payments) ? res.payments : []);
          this.totalCount.set(Number(res?.number_of_records ?? res?.count ?? 0));
          this.totalPages.set(this.calculationService.resolveApiPageCount(res, this.pageSize));
          const clampedPage = this.calculationService.clampPageToApiTotal(this.page(), this.totalPages());
          if (clampedPage != null) {
            this.navigateWithFilters({ page: clampedPage });
            return;
          }
          this.availableBalance.set(Number(res?.available_balance ?? res?.availableBalance ?? 0));
          this.platformBalance.set(Number(res?.platform_balance ?? res?.platformBalance ?? 0));
          const releasedIds = this.extractReleasedPayableIds(res);
          this.payableUspaIds.set(releasedIds);
          this.allUspaIds.set(this.extractAllUspaIds(res));
          this.releasedPayableCount.set(this.resolveReleasedPayableCount(res, releasedIds));
          this.releasedPayableAmount.set(this.resolveReleasedPayableAmount(res));
          this.selectedIds.set([]);
          this.partnerTileSummary.updateFromListResponse(this.productKey, res);
          this.syncLendeeOverviewFromResponse();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (this.accessService.isAccessDenied(err)) {
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
          }
          this.payments.set([]);
          this.totalCount.set(0);
          this.totalPages.set(0);
          this.availableBalance.set(0);
          this.platformBalance.set(0);
          this.payableUspaIds.set([]);
          this.allUspaIds.set([]);
          this.releasedPayableCount.set(0);
          this.releasedPayableAmount.set(0);
          this.selectedIds.set([]);
          this.partnerTileSummary.updateFromListResponse(this.productKey, {});
        },
      });
  }

  /** Numeric id sent to `payment/pay_unpaid_payment_to_user` (`uspa_id` or numeric `payment_id`). */
  rowPayId(payment: UnifiedUnpaidPayment): number | null {
    const raw = payment.uspa_id ?? payment.payment_id;
    if (raw === undefined || raw === null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Display and balance checks use the same basis as legacy (`pay_to_user` first).
   */
  getAmount(payment: UnifiedUnpaidPayment): number {
    return Number(payment.pay_to_user ?? payment.pay_to_doctor ?? payment.amount ?? 0);
  }

  getPartnerLabel(payment: UnifiedUnpaidPayment): string {
    const id = Number(payment['partner_id']);
    if (id === 1) return 'Denefits';
    if (id === 2) return 'Credee';
    if (id === 3) return 'Recuvery';
    if (id === 4) return 'Finance Mutual';
    return '';
  }

  getPaymentTypeLabel(payment: UnifiedUnpaidPayment): string {
    const t = Number(payment['payment_type']);
    if (t === 10) return 'Single';
    if (t === 2) return 'Recurring';
    if (t === 9 || t === 1) return 'Partial';
    if (t === 3) return 'Pay Off';
    if (t === 4) return 'Down';
    if (t === 5) return 'Pay in full';
    return 'Social Payment';
  }

  /** Secondary line under amount in the table (“Partial Payment”, etc.). */
  getPaymentInfoSublabel(payment: UnifiedUnpaidPayment): string {
    if (this.getPaymentStatusLabel(payment) === 'Un-released') {
      return 'Pending Payment';
    }
    const t = Number(payment['payment_type']);
    if (t === 10) return 'Single Payment';
    if (t === 2) return 'Recurring Payment';
    if (t === 9 || t === 1) return 'Partial Payment';
    if (t === 3) return 'Pay Off Payment';
    if (t === 4) return 'Down Payment';
    if (t === 5) return 'Pay in full';
    return 'Social Payment';
  }

  /** Microloan / external id for display; falls back to payment row id. */
  getMicroloanDisplayId(payment: UnifiedUnpaidPayment): string {
    const raw =
      payment['micro_loan_code'] ??
      payment['microloan_id'] ??
      payment['microloanId'] ??
      payment['loan_id'] ??
      payment['loan_reference'] ??
      payment['elf_id'] ??
      payment['reference_id'];
    if (raw !== undefined && raw !== null && raw !== '') {
      return String(raw);
    }
    const fallback = payment['uspa_id'] ?? payment['payment_id'];
    return fallback !== undefined && fallback !== null && fallback !== '' ? String(fallback) : '—';
  }

  getPaymentStatusLabel(payment: UnifiedUnpaidPayment): string {
    const s = Number(payment['payment_status']);
    if (s === 1) return 'Un-released';
    if (s === 2) return 'Released';
    if (s === 3) return 'Paid Automatically';
    if (s === 4) return 'Paid By Admin';
    return '';
  }

  getPaymentDate(payment: UnifiedUnpaidPayment): string {
    return String(payment['date_added'] ?? '-');
  }

  canSelectRow(payment: UnifiedUnpaidPayment): boolean {
    return this.canPayRow(payment);
  }

  canPayRow(payment: UnifiedUnpaidPayment): boolean {
    if (Number(payment['payment_status']) !== 2 || this.rowPayId(payment) === null) {
      return false;
    }
    return !this.calculationService.isRowTransferDisabled(payment);
  }

  onSelectionChange(selectedRows: UnifiedUnpaidPayment[]): void {
    const currentPageIds = this.payments()
      .map((p) => this.rowPayId(p))
      .filter((id): id is number => id !== null);
    const selectedCurrentPageIds = selectedRows
      .map((p) => this.rowPayId(p))
      .filter((id): id is number => id !== null);
    const otherPageIds = this.selectedIds().filter((id) => !currentPageIds.includes(id));
    this.selectedIds.set([...otherPageIds, ...selectedCurrentPageIds]);
  }

  paySingle(payment: UnifiedUnpaidPayment): void {
    if (!this.canPayRow(payment)) return;
    const amount = this.getAmount(payment);
    this.openPaymentConfirmDialog(amount, 1, [payment]);
  }

  paySelected(): void {
    const picked = this.selectedPayablePayments();
    if (!picked.length) return;
    const total = picked.reduce((sum, p) => sum + this.getAmount(p), 0);
    this.openPaymentConfirmDialog(total, picked.length, picked);
  }

  payAllToBusiness(): void {
    const ids = this.allUspaIds();
    if (!ids.length) return;

    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: this.paymentConfirmTitle,
        confirmLabel: 'Pay All',
        cancelLabel: 'Cancel',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;
        const rows = ids.map((uspa_id) => ({ uspa_id }) as UnifiedUnpaidPayment);
        this.payByRows(rows, 0);
      });
  }

  onSubStatusChange(value: number): void {
    this.navigateWithFilters({ subStatus: value, page: 1 });
  }

  resetFilters(): void {
    this.navigateWithFilters({
      search: '',
      startDate: this.defaultStartDate,
      endDate: this.calculationService.unpaidPaymentsTodayYmd(),
      subStatus: -1,
      page: 1,
    });
  }

  onSearch(): void {
    this.navigateWithFilters({
      search: this.search(),
      startDate: this.startDate(),
      endDate: this.endDate(),
      page: 1,
    });
  }

  clearSearch(): void {
    this.navigateWithFilters({ search: '', page: 1 });
  }

  goToPage(next: number): void {
    if (next < 1) return;
    if (next > this.totalPages()) return;
    this.navigateWithFilters({ page: next });
  }

  /** All uspa_ids from the API response. */
  private extractAllUspaIds(res: any): number[] {
    const uspaRoot = res?.uspa_ids ?? res?.uspaIds;
    if (!uspaRoot) return [];

    if (Array.isArray(uspaRoot)) {
      return this.normalizeIdList(uspaRoot);
    }

    if (typeof uspaRoot === 'object') {
      const allIds = uspaRoot.all ?? uspaRoot.ids;
      if (Array.isArray(allIds)) {
        return this.normalizeIdList(allIds);
      }

      const combined: number[] = [];
      for (const key of Object.keys(uspaRoot)) {
        if (Array.isArray(uspaRoot[key])) {
          combined.push(...this.normalizeIdList(uspaRoot[key]));
        }
      }
      return [...new Set(combined)];
    }

    return [];
  }

  /** Released payable ids from the full filtered listing (list API), not the current table page. */
  private extractReleasedPayableIds(res: any): number[] {
    const uspaRoot = res?.uspa_ids ?? res?.uspaIds;
    if (!uspaRoot || typeof uspaRoot !== 'object' || Array.isArray(uspaRoot)) {
      return [];
    }

    const nestedReleased = uspaRoot.released;
    if (Array.isArray(nestedReleased)) {
      return this.normalizeIdList(nestedReleased);
    }

    const statusReleased = uspaRoot['2'] ?? uspaRoot[2];
    if (Array.isArray(statusReleased)) {
      return this.normalizeIdList(statusReleased);
    }

    const allIds = uspaRoot.all ?? uspaRoot.ids;
    const unreleasedIds = uspaRoot.unreleased ?? uspaRoot['1'] ?? uspaRoot[1];
    if (Array.isArray(allIds) && Array.isArray(unreleasedIds)) {
      const unreleasedSet = new Set(this.normalizeIdList(unreleasedIds));
      return this.normalizeIdList(allIds).filter((id) => !unreleasedSet.has(id));
    }

    return [];
  }

  /** Released payable amount from list API totals, not the current table page. */
  private resolveReleasedPayableAmount(res: any): number {
    const releasedAmountKeys = [
      'released_payment_amount',
      'released_payments_amount',
      'payable_amount',
      'total_payable_amount',
      'released_pay_to_user',
      'released_pay_to_users',
      'uspa_ids.released_payment_amount',
      'uspa_ids.released_amount',
      'uspa_ids.released_pay_to_user',
      'uspaIds.released_payment_amount',
      'uspaIds.released_amount',
      'uspaIds.released_pay_to_user',
    ];
    const fromReleasedKeys = this.pickNumberFromResponse(res, releasedAmountKeys, true);
    if (fromReleasedKeys !== null) {
      return fromReleasedKeys;
    }

    const uspaRoot = res?.uspa_ids ?? res?.uspaIds;
    if (uspaRoot && typeof uspaRoot === 'object' && !Array.isArray(uspaRoot)) {
      for (const key of [
        'released_payment_amount',
        'released_amount',
        'released_pay_to_user',
        'released_pay_to_users',
      ]) {
        const n = Number(uspaRoot[key]);
        if (Number.isFinite(n)) {
          return n;
        }
      }
    }

    const genericAmountKeys = [
      'total_pay_to_users',
      'total_pay_to_user',
      'uspa_ids.total_pay_to_user',
      'uspaIds.total_pay_to_user',
    ];
    const fromGenericKeys = this.pickNumberFromResponse(res, genericAmountKeys);
    if (fromGenericKeys !== null) {
      return fromGenericKeys;
    }

    const totalAmount = this.pickNumberFromResponse(res, [
      'total_amount',
      'totalAmount',
      'total_amount_to_pay',
      'payment_due_total',
    ]);
    const platformBalance = Number(res?.platform_balance ?? res?.platformBalance ?? NaN);
    if (totalAmount !== null && Number.isFinite(platformBalance) && totalAmount > platformBalance) {
      return totalAmount - platformBalance;
    }

    return 0;
  }

  private resolveReleasedPayableCount(res: any, releasedIds: number[]): number {
    if (releasedIds.length > 0) {
      return releasedIds.length;
    }

    const countKeys = [
      'released_payment_count',
      'released_payments_count',
      'released_count',
      'uspa_ids.released_count',
      'uspa_ids.released_payment_count',
      'uspaIds.released_count',
      'uspaIds.released_payment_count',
    ];
    const fromKeys = this.pickNumberFromResponse(res, countKeys);
    if (fromKeys !== null) {
      return fromKeys;
    }

    const uspaRoot = res?.uspa_ids ?? res?.uspaIds;
    if (uspaRoot && typeof uspaRoot === 'object' && !Array.isArray(uspaRoot)) {
      for (const key of ['released_count', 'released_payment_count', 'released_payments_count']) {
        const n = Number(uspaRoot[key]);
        if (Number.isFinite(n)) {
          return n;
        }
      }
    }

    return 0;
  }

  private pickNumberFromResponse(res: any, keyPaths: string[], allowZero = false): number | null {
    for (const keyPath of keyPaths) {
      const raw = keyPath.includes('.') ? this.pickNestedValue(res, keyPath) : res?.[keyPath];
      if (raw === undefined || raw === null || raw === '') {
        continue;
      }
      const n = Number(raw);
      if (Number.isFinite(n) && (allowZero || n !== 0)) {
        return n;
      }
    }
    return null;
  }

  private pickNestedValue(root: unknown, keyPath: string): unknown {
    if (root === null || root === undefined) {
      return undefined;
    }
    let cur: unknown = root;
    for (const segment of keyPath.split('.').map((s) => s.trim()).filter(Boolean)) {
      if (cur === null || cur === undefined || typeof cur !== 'object') {
        return undefined;
      }
      cur = (cur as Record<string, unknown>)[segment];
    }
    return cur;
  }

  private normalizeIdList(raw: unknown[]): number[] {
    return raw
      .map((id: unknown) => {
        if (typeof id === 'number' && Number.isFinite(id)) return id;
        const n = Number(id);
        return Number.isFinite(n) ? n : null;
      })
      .filter((id): id is number => id !== null);
  }

  /** Keeps overview card totals aligned with released payable rows (not `platform_balance`). */
  private syncLendeeOverviewFromResponse(): void {
    this.partnerTileSummary.patchOverview(this.productKey, {
      totalPaymentsCount: this.releasedPayableCount(),
      totalPaymentToPay: this.releasedPayableAmount(),
    });
    this.hubOverviewService.cacheProductOverviewFromPage(this.productKey);
  }

  private openPaymentConfirmDialog(
    totalAmount: number,
    paymentCount: number,
    rows: UnifiedUnpaidPayment[]
  ): void {
    const insufficient = totalAmount > this.availableBalance();
    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: this.paymentConfirmTitle,
        totalAmount,
        paymentCount,
        confirmLabel: 'Pay',
        cancelLabel: 'Cancel',
        confirmDisabled: insufficient,
        disabledMessage: insufficient
          ? `Insufficient balance. Amount due is $${totalAmount.toFixed(2)} but available balance is $${this.availableBalance().toFixed(2)}.`
          : undefined,
      })
      .subscribe((result) => {
        if (!result?.confirmed || insufficient) return;
        this.payByRows(rows, totalAmount);
      });
  }

  private payByRows(rows: UnifiedUnpaidPayment[], paidAmount: number): void {
    const payable = rows.filter((p) => this.rowPayId(p) !== null);
    if (!payable.length) return;

    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;

    this.paying.set(true);
    this.adapterService
      .releasePayments(this.productKey, config.productServerName, payable, {
        sub_status: this.subStatus(),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.selectedIds.set([]);
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.selectedIds.set([]);
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => this.fetchPayments());
            return;
          }
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: res?.original_text || res?.message || 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
        error: () => {
          this.paying.set(false);
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
      });
  }

  private showErrorTracking(errors: any[], title: string): void {
    const subtitle = errors
      .map((error, index) => `${index + 1}. ${error?.error_message || error?.message || JSON.stringify(error)}`)
      .join('\n');
    this.unpaidPaymentsModal.openPaymentError({
      title: 'Payment Status',
      statusMessage: title,
      subtitle: subtitle || 'One or more records failed to process.',
      confirmLabel: 'Done',
    });
  }

  private readRouteStateFromSignals(): LendeeListRouteState {
    return {
      search: this.search(),
      page: this.page(),
      subStatus: this.subStatus(),
      startDate: this.startDate(),
      endDate: this.endDate(),
      isDummy: this.isDummyService.isDummy(),
    };
  }

  onStartDateChange(value: string): void {
    const clamped = this.calculationService.clampUnpaidListDateRange(value, this.endDate());
    if (clamped.startDate === this.startDate()) return;
    this.navigateWithFilters({ startDate: clamped.startDate, endDate: clamped.endDate, page: 1 });
  }

  onEndDateChange(value: string): void {
    const clamped = this.calculationService.clampUnpaidListDateRange(this.startDate(), value);
    if (clamped.endDate === this.endDate() && clamped.startDate === this.startDate()) return;
    this.navigateWithFilters({ startDate: clamped.startDate, endDate: clamped.endDate, page: 1 });
  }

  private applyRouteState(state: LendeeListRouteState): void {
    this.isDummyService.syncFromRoute(state.isDummy);
    this.search.set(state.search);
    this.page.set(state.page);
    this.subStatus.set(state.subStatus);
    const clamped = this.calculationService.clampUnpaidListDateRange(state.startDate, state.endDate);
    this.startDate.set(clamped.startDate);
    this.endDate.set(clamped.endDate);
    this.selectedIds.set([]);
  }

  private navigateWithFilters(patch: Partial<LendeeListRouteState>): void {
    const next: LendeeListRouteState = { ...this.readRouteStateFromSignals(), ...patch };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildLendeeListQueryParams(next, this.lendeeRouteDefaults),
      replaceUrl: true,
    });
  }
}
